/*
 * Escrow admin panel — extends the Accountant Workspace with a
 * read + guarded-reversal surface for the canonical PG escrow authority.
 *
 * Endpoints (already implemented, no backend changes):
 *   GET  /api/admin/escrow/balance/{booking_id}     (finance.view)
 *   POST /api/admin/escrow/funding/{event_id}/reverse   (finance.manage)
 *
 * Authority mapping:
 *   Read       → `finance.view`     (both platform and provider accountants)
 *   Reversal   → `finance.manage`   (platform financial correction only)
 * Reuses the existing `finance.manage` permission per the audit's rule
 * "reuse an existing permission that already semantically authorises
 * sensitive financial correction". No new permission introduced.
 *
 * Reversal UX guards (server remains authoritative):
 *   - Reverse button visible only when the caller carries `finance.manage`
 *     AND the funding_event is not already reversed.
 *   - Confirmation modal displays booking_id, funding_event_id, amount +
 *     currency, journal reference, and requires typing REVERSE.
 *   - Frontend never re-computes ledger effects.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { useRBAC } from '../../context/RBACContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Toaster } from '../../components/Toaster';
import { toast } from '../../hooks/use-toast';
import { authHeaders } from '../../utils/unifiedAuth';
import { RefreshCw, ShieldAlert, Undo2, Wallet, HelpCircle } from 'lucide-react';
import DateField from '../../components/DateField';

const API = process.env.REACT_APP_BACKEND_URL || '';

const microsToDecimal = (m, currency = 'USD') => {
  const n = Number(m || 0) / 1_000_000;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch { return `${n.toFixed(2)} ${currency}`; }
};

const isoShort = (v) => { if (!v) return '—'; try { return formatDateTimeLocale(v); } catch { return v; } };
const newKey = (p) => `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

async function apiFetch(path, opts = {}) {
  const headers = { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) };
  const res = await fetch(`${API}${path}`, { ...opts, headers, credentials: 'include' });
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
  return { ok: res.ok, status: res.status, body };
}
function readErr(status, body) {
  if (!body) return `Request failed (${status})`;
  if (typeof body.message === 'string' && body.message) return body.message;
  if (typeof body.detail === 'string' && body.detail) return body.detail;
  if (typeof body.error === 'string' && body.error) return body.error;
  return `Request failed (${status})`;
}

function ReverseDialog({ ev, onClose, onReversed }) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const canGo = typed.trim().toUpperCase() === 'REVERSE';

  const submit = async () => {
    setBusy(true); setErr('');
    const { ok, status, body } = await apiFetch(
      `/api/admin/escrow/funding/${ev.id}/reverse`,
      { method: 'POST', headers: { 'Idempotency-Key': newKey('rev') } },
    );
    setBusy(false);
    if (!ok) { setErr(readErr(status, body)); return; }
    toast({ title: t('adminFinance.escrow.reverseDialog.reversedToast'), description: `${ev.id.slice(0,8)}…` });
    onReversed?.(body?.funding_event);
    onClose?.();
  };

  return (
    <Dialog open={!!ev} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="escrow-reverse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <ShieldAlert className="w-5 h-5" /> {t('adminFinance.escrow.reverseDialog.title')}
          </DialogTitle>
        </DialogHeader>
        <Alert variant="destructive" className="mb-2">
          <AlertTitle>{t('adminFinance.escrow.reverseDialog.warning')}</AlertTitle>
          <AlertDescription>
            {t('adminFinance.escrow.reverseDialog.warningDesc')}
          </AlertDescription>
        </Alert>
        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
          <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.reverseDialog.booking')}</div><div className="font-mono" data-testid="rev-booking">{ev?.booking_id || '—'}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.reverseDialog.event')}</div><div className="font-mono" data-testid="rev-event">{ev?.id?.slice(0,16)}…</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.reverseDialog.amount')}</div><div className="font-semibold" data-testid="rev-amount">{microsToDecimal(ev?.amount_micros, ev?.currency)}</div></div>
          <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.reverseDialog.journalRef')}</div><div className="font-mono text-xs" data-testid="rev-journal">{ev?.journal_entry_id || '—'}</div></div>
        </div>
        <Label htmlFor="rev-confirm">{t('adminFinance.escrow.reverseDialog.typeToConfirm')}</Label>
        <Input id="rev-confirm" value={typed} onChange={(iso) => setTyped(iso)} data-testid="rev-confirm-input" />
        {err && <Alert variant="destructive" className="mt-2" data-testid="rev-error"><AlertTitle>{t('adminFinance.escrow.reverseDialog.refused')}</AlertTitle><AlertDescription>{err}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="rev-cancel">{t('adminFinance.escrow.reverseDialog.cancel')}</Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700"
            onClick={submit}
            disabled={!canGo || busy}
            data-testid="rev-submit"
          >
            <Undo2 className="w-4 h-4 mr-1" /> {busy ? t('adminFinance.escrow.reverseDialog.reversing') : t('adminFinance.escrow.reverseDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EscrowInspectionList({ onSelect }) {
  /**
   * Issue 6 — Owner UAT surface for admin escrow inspection.
   * Reads canonical `/api/finance/admin/escrow/list` (joins escrow_holds
   * × booking_contracts × escrow_milestones, enriched with clinic_profiles
   * brand+legal-entity). No new SSOT.
   *
   * TREATX-OWNER-UAT-ESCROW-FILTER-FIRST-01:
   *   The list no longer auto-loads on mount. Owner must submit at least
   *   one filter (search text, status, or a date range) before results
   *   are fetched. This prevents an unfiltered historical dump when
   *   escrow accumulates across years of production traffic.
   *   Server-side pagination via limit/offset is enforced by the
   *   backend; the UI only shows the current page + a pager.
   */
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [err, setErr] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [hasSearched, setHasSearched] = useState(false);

  const hasAnyFilter = !!(q.trim() || status || dateFrom || dateTo);

  const load = useCallback(async (pageArg = 0) => {
    if (!hasAnyFilter) {
      setErr('Enter at least one filter (search text, status, or date range) before searching.');
      setRows([]); setHasMore(false);
      return;
    }
    setLoading(true); setErr('');
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (status) params.set('status', status);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    params.set('limit', String(pageSize));
    params.set('offset', String(pageArg * pageSize));
    const { ok, status: st, body } = await apiFetch(`/api/finance/admin/escrow/list?${params}`);
    setLoading(false);
    setHasSearched(true);
    if (!ok) { setErr(readErr(st, body)); setRows([]); setHasMore(false); return; }
    setRows(body?.holds || []);
    setHasMore(!!body?.has_more);
    setPage(pageArg);
  }, [q, status, dateFrom, dateTo, pageSize, hasAnyFilter]);

  return (
    <Card data-testid="escrow-inspection-list">
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Wallet className="w-4 h-4 text-slate-500" /> Escrow inspection
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Search (provider brand / legal entity / booking id)</Label>
            <Input
              value={q}
              onChange={(iso) => setQ(iso)}
              placeholder="Acıbadem, TREATX Medikal, BK-…"
              onKeyDown={(e) => { if (e.key === 'Enter') load(0); }}
              data-testid="escrow-inspection-q"
            />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select
              value={status}
              onChange={(iso) => setStatus(iso)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[140px]"
              data-testid="escrow-inspection-status"
            >
              <option value="">— any —</option>
              <option value="pending">pending</option>
              <option value="achieved">achieved</option>
              <option value="released">released</option>
              <option value="reversed">reversed</option>
              <option value="empty">empty</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Contract date from</Label>
            <DateField value={dateFrom} onChange={(iso) => setDateFrom(iso)} className="min-w-[140px]" data-testid="escrow-inspection-date-from" />
          </div>
          <div>
            <Label className="text-xs">Contract date to</Label>
            <DateField value={dateTo} onChange={(iso) => setDateTo(iso)} className="min-w-[140px]" data-testid="escrow-inspection-date-to" />
          </div>
          <div className="self-end">
            <Button onClick={() => load(0)} disabled={loading || !hasAnyFilter} size="sm" data-testid="escrow-inspection-load">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Search
            </Button>
          </div>
        </div>

        {err && <div className="text-xs text-rose-700 mb-2" data-testid="escrow-inspection-err">{err}</div>}

        {!hasSearched && !err && (
          <div className="p-4 text-center text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-md" data-testid="escrow-inspection-prompt">
            Enter at least one filter above (search text, status, or contract-date range) and press <strong>Search</strong>.
            Escrow data can accumulate across years — the full history is not loaded automatically.
          </div>
        )}

        {hasSearched && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-2 py-1.5">Provider</th>
                <th className="text-left px-2 py-1.5">Booking</th>
                <th className="text-right px-2 py-1.5">Contracted</th>
                <th className="text-right px-2 py-1.5">Milestones</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-left px-2 py-1.5">Last activity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-500" data-testid="escrow-inspection-empty">
                  No holds match this filter.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.escrow_hold_id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`escrow-inspection-row-${r.booking_id}`}>
                  <td className="px-2 py-1.5">
                    <div className="font-medium text-slate-900">
                      {/* TREATX-OWNER-UAT-ESCROW-PROVIDER-IDENTITY-01:
                          Primary display = Brand / Trading Name; secondary =
                          Legal Entity Name; technical clinic_id is now a
                          small monospace footnote only, never the primary
                          human-facing identity. */}
                      {r.provider_brand || r.provider_name || r.provider_legal_entity || <span className="text-slate-400 italic">unnamed provider</span>}
                    </div>
                    {r.provider_legal_entity && r.provider_legal_entity !== r.provider_brand && (
                      <div className="text-[11px] text-slate-600 truncate max-w-[240px]" title={r.provider_legal_entity}>
                        {r.provider_legal_entity}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 font-mono" title="internal technical id">{r.clinic_id}</div>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[11px]" title={r.booking_id}>{r.booking_id?.slice(0, 20)}…</td>
                  <td className="px-2 py-1.5 text-right">
                    {microsToDecimal((r.contracted_amount_minor_units || 0) * 10000, r.currency) || '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right">{r.milestone_count}</td>
                  <td className="px-2 py-1.5">
                    <Badge className="text-[10px]" variant="outline">{r.status_summary}</Badge>
                  </td>
                  <td className="px-2 py-1.5 text-slate-500 text-[11px]">{isoShort(r.last_activity_utc)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <Button size="sm" variant="outline"
                      onClick={() => onSelect(r.booking_id)}
                      data-testid={`escrow-inspection-inspect-${r.booking_id}`}
                    >
                      Inspect
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {hasSearched && (rows.length > 0 || hasMore || page > 0) && (
          <div className="mt-3 flex items-center justify-between text-xs text-slate-600" data-testid="escrow-inspection-pager">
            <div>
              Page <strong>{page + 1}</strong> · showing {rows.length} hold(s){hasMore ? ' (more available)' : ''}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page === 0 || loading} onClick={() => load(page - 1)} data-testid="escrow-inspection-page-prev">Previous</Button>
              <Button size="sm" variant="outline" disabled={!hasMore || loading} onClick={() => load(page + 1)} data-testid="escrow-inspection-page-next">Next</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function EscrowPanel() {
  const { t } = useTranslation();
  const rbac = useRBAC();
  const canReverse = rbac?.can?.('finance.manage') ?? false;
  const canView = rbac?.can?.('finance.view') ?? canReverse;

  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [events, setEvents] = useState([]);
  const [err, setErr] = useState('');
  const [reverseTarget, setReverseTarget] = useState(null);
  // TREATX-OWNER-UAT-ESCROW-INSPECT-VISIBILITY-01: Inspect must
  // visibly move the user to the detail area and give them a clear
  // result — including when the booking genuinely has zero funding
  // events. Ref used for scrollIntoView; `inspectLoaded` guards the
  // clarifying "loaded" banner.
  const detailRef = React.useRef(null);
  const [inspectLoaded, setInspectLoaded] = useState(false);

  const load = useCallback(async (bidOverride) => {
    const bid = (bidOverride ?? bookingId ?? '').trim();
    if (!bid) return;
    setLoading(true); setErr(''); setInspectLoaded(false);
    const { ok, status, body } = await apiFetch(`/api/admin/escrow/balance/${encodeURIComponent(bid)}`);
    setLoading(false);
    if (!ok) {
      setErr(readErr(status, body));
      setBalance(null);
      setEvents([]);
      return;
    }
    setBalance(body?.balance || null);
    setEvents(body?.funding_events || []);
    setInspectLoaded(true);
    // Focus & scroll to the detail area so Inspect isn't a silent action.
    setTimeout(() => {
      try { detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* noop */ }
    }, 60);
  }, [bookingId]);

  const handleInspect = useCallback((bid) => {
    setBookingId(bid);
    load(bid);
  }, [load]);

  if (!canView) {
    return (
      <div className="p-6 max-w-lg mx-auto" data-testid="escrow-forbidden">
        <Card><CardHeader><CardTitle>Escrow</CardTitle></CardHeader>
          <CardContent><p className="text-sm">Your role does not have <code>finance.view</code>.</p></CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="escrow-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Wallet className="w-5 h-5 text-slate-500" />
          <h1 className="text-xl font-semibold">{t('adminFinance.escrow.title')}</h1>
          <Badge className="bg-slate-200 text-slate-700" data-testid="escrow-scope-badge">
            {canReverse ? t('adminFinance.escrow.scope.reversal') : t('adminFinance.escrow.scope.readOnly')}
          </Badge>
        </div>

        {/* OWNER UAT — plain-language intro so Owner understands purpose
            before seeing a Booking ID text field. No new authority. */}
        <Alert data-testid="escrow-intro">
          <HelpCircle className="w-4 h-4" />
          <AlertTitle>{t('adminFinance.escrow.title')}</AlertTitle>
          <AlertDescription>
            <p className="mb-1">{t('adminFinance.escrow.intro')}</p>
            <p className="text-slate-500">{t('adminFinance.escrow.help')}</p>
          </AlertDescription>
        </Alert>

        {/* Issue 6 (Owner UAT) — searchable escrow inspection list.
            Admins land here without needing a Booking ID. Rows are
            projected from the canonical `escrow_holds` × `booking_contracts`
            × `escrow_milestones` truth (see backend
            `/api/finance/admin/escrow/list`). Click any row → prefills the
            Booking ID and loads the detail below. */}
        <EscrowInspectionList onSelect={handleInspect} />

        <Card ref={detailRef}>
          <CardContent className="p-3 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs">{t('adminFinance.escrow.bookingLabel')}</Label>
              <Input value={bookingId} onChange={(iso) => setBookingId(iso)} placeholder="booking-…" data-testid="escrow-booking-input" />
            </div>
            <Button onClick={() => load()} disabled={loading || !bookingId} data-testid="escrow-load-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> {t('adminFinance.escrow.load')}
            </Button>
          </CardContent>
        </Card>

        {inspectLoaded && !err && (
          <Alert data-testid="escrow-inspect-loaded-banner">
            <HelpCircle className="w-4 h-4" />
            <AlertTitle>Inspection loaded</AlertTitle>
            <AlertDescription>
              Showing canonical escrow balance and funding events for booking
              <code className="mx-1">{bookingId.slice(0,24)}{bookingId.length > 24 ? '…' : ''}</code>.
              {events.length === 0 && (
                <span> This booking currently has <strong>no funding events</strong> — either the milestones have not yet been achieved, or no funds have been recorded. This is expected for a booking that has not been paid yet.</span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {err && <Alert variant="destructive" data-testid="escrow-error"><AlertTitle>Load failed</AlertTitle><AlertDescription>{err}</AlertDescription></Alert>}

        {balance && (
          <Card>
            <CardHeader className="py-3"><CardTitle className="text-sm">{t('adminFinance.escrow.balance.title')} · {balance.booking_id?.slice(0,16)}…</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.balance.funded')}</div><div className="font-semibold" data-testid="escrow-funded">{microsToDecimal(balance.funded_micros, balance.currency)}</div></div>
              <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.balance.released')}</div><div className="font-semibold" data-testid="escrow-released">{microsToDecimal(balance.released_micros, balance.currency)}</div></div>
              <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.balance.available')}</div><div className="font-semibold" data-testid="escrow-available">{microsToDecimal(balance.available_micros, balance.currency)}</div></div>
              <div><div className="text-[11px] uppercase text-slate-500">{t('adminFinance.escrow.balance.currency')}</div><div className="font-semibold" data-testid="escrow-currency">{balance.currency || '—'}</div></div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">{t('adminFinance.escrow.events.title')} · {events.length}</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="escrow-events-table">
              <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">{t('adminFinance.escrow.col.event')}</th>
                  <th className="text-left px-3 py-2">{t('adminFinance.escrow.col.payer')}</th>
                  <th className="text-right px-3 py-2">{t('adminFinance.escrow.col.amount')}</th>
                  <th className="text-left px-3 py-2">{t('adminFinance.escrow.col.status')}</th>
                  <th className="text-left px-3 py-2">{t('adminFinance.escrow.col.journal')}</th>
                  <th className="text-left px-3 py-2">{t('adminFinance.escrow.col.received')}</th>
                  <th className="text-right px-3 py-2">{t('adminFinance.escrow.col.action')}</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500" data-testid="escrow-empty">
                    {balance ? t('adminFinance.escrow.empty.noEvents') : t('adminFinance.escrow.empty.noBooking')}
                  </td></tr>
                )}
                {events.map((ev) => {
                  const isReversed = (ev.status || '').toLowerCase() === 'reversed';
                  return (
                    <tr key={ev.id} className="border-b" data-testid={`escrow-row-${ev.id?.slice(0,8)}`}>
                      <td className="px-3 py-2 font-mono text-xs">{ev.id?.slice(0,8)}…</td>
                      <td className="px-3 py-2 text-xs">{ev.payer_type || '—'}</td>
                      <td className="px-3 py-2 text-right">{microsToDecimal(ev.amount_micros, ev.currency)}</td>
                      <td className="px-3 py-2">
                        <Badge className={isReversed ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}>
                          {ev.status || 'recorded'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{(ev.journal_entry_id || '').slice(0, 12)}…</td>
                      <td className="px-3 py-2 text-xs">{isoShort(ev.received_at_utc || ev.created_at_utc)}</td>
                      <td className="px-3 py-2 text-right">
                        {canReverse && !isReversed && (
                          <Button size="sm" variant="outline" className="border-rose-300 text-rose-700"
                            onClick={() => setReverseTarget(ev)}
                            data-testid={`escrow-reverse-btn-${ev.id?.slice(0,8)}`}>
                            <Undo2 className="w-3 h-3 mr-1" /> {t('adminFinance.escrow.reverse')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <p className="text-[11px] text-slate-500">
          {t('adminFinance.escrow.ledgerNote')}
        </p>
      </div>
      <ReverseDialog ev={reverseTarget} onClose={() => setReverseTarget(null)} onReversed={() => load()} />
      <Toaster />
    </div>
  );
}